import type { DragEvent } from "react";
import { useCallback, useState } from "react";

interface DraggedSkillItem {
  path: string;
  type: "file" | "folder";
}

interface UseRootDragAndDropParams {
  readOnly: boolean;
  onMoveNode?: (
    sourcePath: string,
    sourceType: "file" | "folder",
    targetFolderPath: string | null,
  ) => void;
}

export const useRootDragAndDrop = ({
  readOnly,
  onMoveNode,
}: UseRootDragAndDropParams) => {
  const [isRootDragOver, setIsRootDragOver] = useState(false);
  const [draggedItem, setDraggedItem] = useState<DraggedSkillItem | null>(null);

  const clearDragState = useCallback(() => {
    setIsRootDragOver(false);
    setDraggedItem(null);
  }, []);

  const handleRootDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (readOnly || !onMoveNode) {
        return;
      }

      if (event.target !== event.currentTarget) {
        setIsRootDragOver(false);
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setIsRootDragOver(true);
    },
    [onMoveNode, readOnly],
  );

  const handleRootDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null;
    if (related && (event.currentTarget as Node).contains(related)) {
      return;
    }

    setIsRootDragOver(false);
  }, []);

  const handleRootDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (readOnly || !onMoveNode) {
        return;
      }

      if (event.target !== event.currentTarget) {
        setIsRootDragOver(false);
        return;
      }

      event.preventDefault();
      clearDragState();

      const sourcePath = event.dataTransfer.getData("text/skill-path");
      const sourceType = event.dataTransfer.getData("text/skill-type") as "file" | "folder";
      if (!sourcePath || !sourceType) {
        return;
      }

      onMoveNode(sourcePath, sourceType, null);
    },
    [clearDragState, onMoveNode, readOnly],
  );

  return {
    draggedItem,
    isRootDragOver,
    setDraggedItem,
    clearDragState,
    setIsRootDragOver,
    handleRootDragOver,
    handleRootDragLeave,
    handleRootDrop,
  };
};
